'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/Spinner';
import { Logo } from '../components/Logo';
import {
  StyledLandingPage,
  StyledNav,
  StyledNavInner,
  StyledNavLogoArea,
  StyledNavActions,
  StyledNavLoginBtn,
  StyledNavCTABtn,
  StyledHeroSection,
  StyledHeroInner,
  StyledHeroBadge,
  StyledHeroTitle,
  StyledHeroTitleAccent,
  StyledHeroSubtitle,
  StyledHeroCTARow,
  StyledHeroPrimaryBtn,
  StyledMockupWrapper,
  StyledMockupFrame,
  StyledMockupBar,
  StyledMockupDot,
  StyledMockupUrlBar,
  StyledMockupBody,
  StyledMockupSidebar,
  StyledMockupSidebarItem,
  StyledMockupSidebarItemActive,
  StyledMockupMain,
  StyledMockupHeaderBar,
  StyledMockupListRow,
  StyledMockupCard,
  StyledMockupCardImage,
  StyledMockupCardLabel,
  StyledFeaturesSection,
  StyledFeaturesInner,
  StyledFeaturesLabel,
  StyledFeaturesTitle,
  StyledFeaturesUnderline,
  StyledFeaturesGrid,
  StyledFeatureCard,
  StyledFeatureCardIcon,
  StyledFeatureCardTitle,
  StyledFeatureCardDesc,
  StyledTestimonialSection,
  StyledTestimonialInner,
  StyledQuoteMark,
  StyledQuoteText,
  StyledJoinSection,
  StyledJoinInner,
  StyledJoinTitle,
  StyledJoinSubtitle,
  StyledJoinButton,
  StyledFooter,
  StyledFooterInner,
  StyledFooterTop,
  StyledFooterLogoArea,
  StyledFooterTagline,
  StyledFooterColumn,
  StyledFooterColumnTitle,
  StyledFooterLink,
  StyledFooterBottom,
  StyledFooterCopyright,
} from './page.styled';

export default function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.push('/dashboard');
    }
  }, [isAuthenticated, isLoading, router]);

  if (isLoading) {
    return <Spinner fullPage size="lg" label="Loading..." />;
  }

  if (isAuthenticated) {
    return null;
  }

  return (
    <StyledLandingPage>
      {/* ── Nav ─────────────────────────────────────────────── */}
      <StyledNav>
        <StyledNavInner>
          <StyledNavLogoArea onClick={() => router.push('/')}>
            <Logo size="sm" showText />
          </StyledNavLogoArea>

          <StyledNavActions>
            <StyledNavLoginBtn onClick={() => router.push('/login')}>
              Log In
            </StyledNavLoginBtn>
            <StyledNavCTABtn onClick={() => router.push('/signup')}>
              Get Started
            </StyledNavCTABtn>
          </StyledNavActions>
        </StyledNavInner>
      </StyledNav>

      {/* ── Hero ────────────────────────────────────────────── */}
      <StyledHeroSection>
        <StyledHeroInner>
          <StyledHeroBadge>✦ The Premium Family Organizer</StyledHeroBadge>

          <StyledHeroTitle>
            Your Family Sanctuary,{' '}
            <br />
            <StyledHeroTitleAccent>Organized.</StyledHeroTitleAccent>
          </StyledHeroTitle>

          <StyledHeroSubtitle>
            Bring harmony to your home with a unified dashboard for meals,
            schedules, and budgeting. Experience the peace of a well-managed
            life.
          </StyledHeroSubtitle>

          <StyledHeroCTARow>
            <StyledHeroPrimaryBtn onClick={() => router.push('/signup')}>
              Get Started
            </StyledHeroPrimaryBtn>
          </StyledHeroCTARow>

          {/* App mockup */}
          <StyledMockupWrapper>
            <StyledMockupFrame>
              <StyledMockupBar>
                <StyledMockupDot style={{ background: '#FF5F57' }} />
                <StyledMockupDot style={{ background: '#FEBC2E' }} />
                <StyledMockupDot style={{ background: '#28C840' }} />
                <StyledMockupUrlBar />
              </StyledMockupBar>
              <StyledMockupBody>
                <StyledMockupSidebar>
                  <StyledMockupSidebarItem />
                  <StyledMockupSidebarItem />
                  <StyledMockupSidebarItemActive />
                  <StyledMockupSidebarItem />
                  <StyledMockupSidebarItem />
                </StyledMockupSidebar>
                <StyledMockupMain>
                  <StyledMockupHeaderBar />
                  <StyledMockupListRow style={{ width: '70%' }} />
                  <StyledMockupListRow style={{ width: '55%' }} />
                  <StyledMockupCard>
                    <StyledMockupCardImage>
                      <StyledMockupCardLabel>
                        Tuscan Butter Salmon
                      </StyledMockupCardLabel>
                    </StyledMockupCardImage>
                  </StyledMockupCard>
                </StyledMockupMain>
              </StyledMockupBody>
            </StyledMockupFrame>
          </StyledMockupWrapper>
        </StyledHeroInner>
      </StyledHeroSection>

      {/* ── Features ────────────────────────────────────────── */}
      <StyledFeaturesSection>
        <StyledFeaturesInner>
          <StyledFeaturesLabel>✦ Premium Features</StyledFeaturesLabel>
          <StyledFeaturesTitle>Designed for Modern Families</StyledFeaturesTitle>
          <StyledFeaturesUnderline />

          <StyledFeaturesGrid>
            <StyledFeatureCard>
              <StyledFeatureCardIcon>
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8h1a4 4 0 010 8h-1" />
                  <path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" />
                  <line x1="6" y1="1" x2="6" y2="4" />
                  <line x1="10" y1="1" x2="10" y2="4" />
                  <line x1="14" y1="1" x2="14" y2="4" />
                </svg>
              </StyledFeatureCardIcon>
              <StyledFeatureCardTitle>Gourmet Meal Planning</StyledFeatureCardTitle>
              <StyledFeatureCardDesc>
                Curate weekly menus with drag-and-drop simplicity.
                Auto-generate grocery lists from your favorite recipes.
              </StyledFeatureCardDesc>
            </StyledFeatureCard>

            <StyledFeatureCard>
              <StyledFeatureCardIcon>
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </StyledFeatureCardIcon>
              <StyledFeatureCardTitle>Unified Schedules</StyledFeatureCardTitle>
              <StyledFeatureCardDesc>
                Sync everyone&apos;s calendar in one beautiful view.
                Color-coded events ensure no soccer practice is ever missed.
              </StyledFeatureCardDesc>
            </StyledFeatureCard>

            <StyledFeatureCard>
              <StyledFeatureCardIcon>
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 001.98 1.61h9.72a2 2 0 001.98-1.61L23 6H6" />
                </svg>
              </StyledFeatureCardIcon>
              <StyledFeatureCardTitle>Smart Shopping Lists</StyledFeatureCardTitle>
              <StyledFeatureCardDesc>
                Automatically build your grocery list from your weekly meal
                plan. Always know what to buy and never run out.
              </StyledFeatureCardDesc>
            </StyledFeatureCard>
          </StyledFeaturesGrid>
        </StyledFeaturesInner>
      </StyledFeaturesSection>

      {/* ── Testimonial ─────────────────────────────────────── */}
      <StyledTestimonialSection>
        <StyledTestimonialInner>
          <StyledQuoteMark>&#10077;</StyledQuoteMark>
          <StyledQuoteText>
            &ldquo;planit.family transformed our chaotic evenings into peaceful
            family time. It&apos;s not just an app; it&apos;s the heartbeat of
            our home.&rdquo;
          </StyledQuoteText>
        </StyledTestimonialInner>
      </StyledTestimonialSection>

      {/* ── Join CTA ────────────────────────────────────────── */}
      <StyledJoinSection>
        <StyledJoinInner>
          <StyledJoinTitle>Join the Family</StyledJoinTitle>
          <StyledJoinSubtitle>
            Start your journey towards a calmer, more connected home life today.
          </StyledJoinSubtitle>
          <StyledJoinButton onClick={() => router.push('/signup')}>
            Get Started Free
          </StyledJoinButton>
        </StyledJoinInner>
      </StyledJoinSection>

      {/* ── Footer ──────────────────────────────────────────── */}
      <StyledFooter>
        <StyledFooterInner>
          <StyledFooterTop>
            <StyledFooterLogoArea>
              <Logo size="sm" showText />
              <StyledFooterTagline>
                Crafting digital tools for the modern family.
              </StyledFooterTagline>
            </StyledFooterLogoArea>

            <StyledFooterColumn>
              <StyledFooterColumnTitle>Product</StyledFooterColumnTitle>
              <StyledFooterLink>Features</StyledFooterLink>
              <StyledFooterLink>Pricing</StyledFooterLink>
              <StyledFooterLink>Integrations</StyledFooterLink>
            </StyledFooterColumn>

            <StyledFooterColumn>
              <StyledFooterColumnTitle>Company</StyledFooterColumnTitle>
              <StyledFooterLink>About Us</StyledFooterLink>
              <StyledFooterLink>Careers</StyledFooterLink>
              <StyledFooterLink>Privacy Policy</StyledFooterLink>
            </StyledFooterColumn>

            <StyledFooterColumn>
              <StyledFooterColumnTitle>Social</StyledFooterColumnTitle>
              <StyledFooterLink>LinkedIn</StyledFooterLink>
              <StyledFooterLink>Twitter</StyledFooterLink>
            </StyledFooterColumn>
          </StyledFooterTop>

          <StyledFooterBottom>
            <StyledFooterCopyright>
              &copy; 2026 planit.family Inc. All rights reserved.
            </StyledFooterCopyright>
          </StyledFooterBottom>
        </StyledFooterInner>
      </StyledFooter>
    </StyledLandingPage>
  );
}
